export default function Features() {
  return (
    <section id="features" className="py-24 bg-gradient-to-b from-[#F0F4F8] to-[#E8F1F5] relative">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-5xl font-serif text-[#2F7FB7]">Core Features</h2>
          <p className="text-xl text-[#4A5568] max-w-3xl mx-auto leading-relaxed">
            A carefully curated collection of therapeutic tools designed to help you feel more grounded, more in control, and more like yourself — whenever and wherever you need support.
          </p>
           <p className="text-xl text-[#4A5568] max-w-3xl mx-auto leading-relaxed">
           Each feature is created with simplicity, clarity, and your emotional safety in mind.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 auto-rows-auto">
          {/* Self-Assessments - Large card spanning 2 columns */}
          <div className="md:col-span-2 bg-white rounded-[28px] p-10 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-[#E8F1F5] mb-6">
              <i className="ri-file-list-3-line text-3xl text-[#2F7FB7]"></i>
            </div>
            <h3 className="text-3xl font-serif text-[#2F7FB7] mb-4">Self-Assessments</h3>
            <p className="text-[#4A5568] leading-relaxed mb-6 text-lg">
              A private, gentle space designed to help you better understand your emotional wellbeing. These tools offer insight into what you may be experiencing and which areas need extra care.
            </p>
            <ul className="space-y-3 mb-8">
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 rounded-full bg-[#5A9FD4] mt-2 flex-shrink-0"></span>
                <span className="text-[#4A5568]">Gain clarity around emotional patterns and symptoms</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 rounded-full bg-[#5A9FD4] mt-2 flex-shrink-0"></span>
                <span className="text-[#4A5568]">Notice what is improving and what still needs support</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 rounded-full bg-[#5A9FD4] mt-2 flex-shrink-0"></span>
                <span className="text-[#4A5568]">Understand how stress, sleep, lifestyle, and triggers affect you</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 rounded-full bg-[#5A9FD4] mt-2 flex-shrink-0"></span>
                <span className="text-[#4A5568]">Connect more easily with tools that suit your specific needs</span>
              </li>
            </ul>
            <a href="/download" className="text-[#2F7FB7] font-medium inline-flex items-center gap-2 hover:gap-3 transition-all cursor-pointer whitespace-nowrap group">
              Explore Assessments
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
            </a>
          </div>

          {/* Mood Tracker - Vertical card */}
          <div className="bg-white rounded-[28px] p-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
            <div className="flex items-center justify-center w-14 h-14 rounded-full bg-[#E8F1F5] mb-6">
              <i className="ri-line-chart-line text-2xl text-[#2F7FB7]"></i>
            </div>
            <h3 className="text-2xl font-serif text-[#2F7FB7] mb-4">Mood & Anxiety Tracker</h3>
            <p className="text-[#4A5568] leading-relaxed mb-6">
              Stay gently connected to your emotional wellbeing over time. Notice patterns, changes, and progress with kindness and curiosity.
            </p>
            <div className="bg-gradient-to-br from-[#E8F1F5] to-[#D1E3EA] rounded-2xl p-6 mb-6">
              <div className="flex items-end justify-between h-32 gap-2">
                <div className="w-full bg-[#2F7FB7]/40 rounded-t-lg" style={{ height: '45%' }}></div>
                <div className="w-full bg-[#2F7FB7]/60 rounded-t-lg" style={{ height: '65%' }}></div>
                <div className="w-full bg-[#2F7FB7]/50 rounded-t-lg" style={{ height: '55%' }}></div>
                <div className="w-full bg-[#5A9FD4]/70 rounded-t-lg" style={{ height: '75%' }}></div>
                <div className="w-full bg-[#2F7FB7] rounded-t-lg" style={{ height: '85%' }}></div>
              </div>
            </div>
            <a href="/download" className="text-[#2F7FB7] font-medium inline-flex items-center gap-2 hover:gap-3 transition-all cursor-pointer whitespace-nowrap group">
              Start Tracking
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
            </a>
          </div>

          {/* Masterclass - Card with slight rotation */}
          <div className="bg-gradient-to-br from-white to-[#F0F4F8] rounded-[28px] p-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 hover:rotate-0 rotate-1 relative">
            <span className="absolute top-4 right-4 bg-[#5A9FD4] text-white text-xs px-3 py-1 rounded-full whitespace-nowrap">Featured</span>
            <div className="flex items-center justify-center w-14 h-14 rounded-full bg-[#E8F1F5] mb-6">
              <i className="ri-graduation-cap-line text-2xl text-[#2F7FB7]"></i>
            </div>
            <h3 className="text-2xl font-serif text-[#2F7FB7] mb-4">Masterclass Series</h3>
            <p className="text-[#4A5568] leading-relaxed mb-6">
              Professionally developed lessons introducing practical strategies for understanding and managing mental health in everyday life.
            </p>
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3">
                <i className="ri-checkbox-circle-fill text-[#5A9FD4]"></i>
                <span className="text-sm text-[#4A5568]">Anxiety Management (10 parts)</span>
              </div>
              <div className="flex items-center gap-3">
                <i className="ri-time-line text-[#6B7280]"></i>
                <span className="text-sm text-[#6B7280]">Depression (Coming Soon)</span>
              </div>
              <div className="flex items-center gap-3">
                <i className="ri-time-line text-[#6B7280]"></i>
                <span className="text-sm text-[#6B7280]">Burnout & More (Coming Soon) </span>
              </div>
            </div>
            <a href="/download" className="text-[#2F7FB7] font-medium inline-flex items-center gap-2 hover:gap-3 transition-all cursor-pointer whitespace-nowrap group">
              View Masterclasses
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
            </a>
          </div>

          {/* Additional features preview */}
          <div className="md:col-span-2 bg-white/60 backdrop-blur-sm rounded-[28px] p-8 shadow-lg">
            <div className="grid grid-cols-3 gap-6">
              <div className="space-y-4">
                <div className="relative h-40 rounded-2xl overflow-hidden mb-4">
                  <img 
                    src="https://readdy.ai/api/search-image?query=peaceful%20serene%20abstract%20illustration%20of%20gentle%20sound%20waves%20and%20musical%20notes%20flowing%20through%20nature%20forest%20with%20soft%20sage%20green%20and%20lavender%20colors%20representing%20calming%20music%20therapy%20and%20relaxation%20simple%20minimalist%20design%20with%20theta%20delta%20wave%20patterns&width=400&height=320&seq=music-hub-feature-card&orientation=squarish"
                    alt="Music Hub"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2F7FB7]/80 to-transparent flex items-end p-4">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-white/90">
                      <i className="ri-music-2-line text-xl text-[#2F7FB7]"></i>
                    </div>
                  </div>
                </div>
                <h4 className="font-semibold text-lg text-[#2F7FB7]">Music Hub</h4>
                <p className="text-sm text-[#4A5568] leading-relaxed">Nature-inspired soundscapes with Theta and Delta waves for deep relaxation and restorative sleep</p>
                <a href="/download" className="text-[#2F7FB7] text-sm font-medium inline-flex items-center gap-2 hover:gap-3 transition-all cursor-pointer whitespace-nowrap group">
                  Explore Music
                  <i className="ri-arrow-right-line text-sm group-hover:translate-x-1 transition-transform"></i>
                </a>
              </div>

              <div className="space-y-4">
                <div className="relative h-40 rounded-2xl overflow-hidden mb-4">
                  <img 
                    src="https://readdy.ai/api/search-image?query=tranquil%20peaceful%20nature%20scene%20with%20flowing%20river%20gentle%20waterfall%20quiet%20forest%20and%20soft%20sunlight%20through%20trees%20calming%20visual%20meditation%20background%20in%20soft%20sage%20green%20and%20lavender%20tones%20simple%20serene%20composition%20for%20relaxation%20and%20mindfulness&width=400&height=320&seq=video-hub-feature-card&orientation=squarish"
                    alt="Video Hub"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2F7FB7]/80 to-transparent flex items-end p-4">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-white/90">
                      <i className="ri-video-line text-xl text-[#2F7FB7]"></i>
                    </div>
                  </div>
                </div>
                <h4 className="font-semibold text-lg text-[#2F7FB7]">Video Hub</h4>
                <p className="text-sm text-[#4A5568] leading-relaxed">Tranquil nature scenes paired with soothing music for anxiety relief and mindful breaks</p>
                <a href="/download" className="text-[#2F7FB7] text-sm font-medium inline-flex items-center gap-2 hover:gap-3 transition-all cursor-pointer whitespace-nowrap group">
                  Watch Videos
                  <i className="ri-arrow-right-line text-sm group-hover:translate-x-1 transition-transform"></i>
                </a>
              </div>

              <div className="space-y-4">
                <div className="relative h-40 rounded-2xl overflow-hidden mb-4">
                  <img 
                    src="https://readdy.ai/api/search-image?query=inspiring%20uplifting%20abstract%20illustration%20showing%20light%20rays%20breaking%20through%20clouds%20symbolizing%20hope%20clarity%20and%20personal%20growth%20with%20soft%20sage%20green%20and%20warm%20golden%20tones%20representing%20positive%20mindset%20and%20mental%20wellness%20journey%20simple%20elegant%20design&width=400&height=320&seq=weekly-thoughts-feature-card&orientation=squarish"
                    alt="Weekly Thoughts"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2F7FB7]/80 to-transparent flex items-end p-4">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-white/90">
                      <i className="ri-lightbulb-line text-xl text-[#2F7FB7]"></i>
                    </div>
                  </div>
                </div>
                <h4 className="font-semibold text-lg text-[#2F7FB7]">Weekly Thoughts</h4>
                <p className="text-sm text-[#4A5568] leading-relaxed">Uplifting messages to help you reconnect with your potential, focus, and clarity</p>
                <a href="/download" className="text-[#2F7FB7] text-sm font-medium inline-flex items-center gap-2 hover:gap-3 transition-all cursor-pointer whitespace-nowrap group">
                  Read More
                  <i className="ri-arrow-right-line text-sm group-hover:translate-x-1 transition-transform"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
